import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import { listProposalSections, createProposalSection, updateProposalSection, deleteProposalSection } from "../../../services/proposalSectionsApi";
import api, { getCurrentUser } from "../../chat/api";
import { PiPlus, PiRows, PiListBullets, PiMagnifyingGlass, PiDotsThree } from "react-icons/pi";
import { PageHeader, PrimaryButton, StatusPill, ViewOnlyBanner, LockedField, useSettings } from "../settings/ui";

// Settings > My Organization > Templates > Proposal Sections.
// Table of reusable proposal section templates (TOC scaffolds).
// Data: GET /api/v1/rfp/proposal-templates/?organization_id=<selected_org_id>.
// The API returns only created_by_user_id, so we resolve names from the org's
// member list (/api/v1/org/details) and mark the current user "(You)".

const RowActionMenu = ({ pos, onEdit, onDelete }) =>
  createPortal(
    <div
      className="fixed z-[10000] w-[160px] bg-white border border-black/10 rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-1 font-outfit"
      style={{ top: pos.top, left: pos.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <button onClick={onEdit} className="w-full flex items-center px-2.5 h-8 rounded-[6px] font-outfit text-[14px] text-[#3F3F46] hover:bg-[#F4F4F5] transition-colors">
        Edit
      </button>
      <button onClick={onDelete} className="w-full flex items-center px-2.5 h-8 rounded-[6px] font-outfit text-[14px] text-[#E21919] hover:bg-[rgba(226,25,25,0.06)] transition-colors">
        Delete
      </button>
    </div>,
    document.body
  );

const DeleteConfirmModal = ({ name, deleting, onClose, onConfirm }) =>
  createPortal(
    <div className="fixed inset-0 z-[10001] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[16px] shadow-xl w-full max-w-[440px] p-6 font-outfit text-center" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[18px] font-semibold text-[#27272A]">Delete “{name}”?</h2>
        <p className="text-[14px] text-[#71717A] mt-1.5">This template will be permanently removed. This can't be undone.</p>
        <div className="flex justify-center gap-3 mt-6">
          <button onClick={onClose} className="px-7 py-2.5 rounded-[8px] border border-[#E4E4E7] text-[#3F3F46] text-[14px] font-medium hover:bg-[#F4F4F5]">Cancel</button>
          <button
            disabled={deleting}
            onClick={onConfirm}
            className={`px-7 py-2.5 rounded-[8px] text-white text-[14px] font-medium ${deleting ? "bg-[#E4A0A0] cursor-not-allowed" : "bg-[#DC2626] hover:bg-[#B91C1C]"}`}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

// Up/down chevrons for the created-date sort toggle; the active direction is
// darkened.
const SortArrows = ({ dir }) => (
  <span className="inline-flex flex-col justify-center gap-[2px] leading-none">
    <svg width="10" height="5" viewBox="0 0 10 5" fill="none" className={dir === "asc" ? "text-[#3F3F46]" : "text-[#C4C4C8]"}>
      <path d="M1 4L5 1L9 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <svg width="10" height="5" viewBox="0 0 10 5" fill="none" className={dir === "desc" ? "text-[#3F3F46]" : "text-[#C4C4C8]"}>
      <path d="M1 1L5 4L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

// Read-only side drawer listing a template's sections and their subsections.
// Slides in from the right over a dimmed backdrop.
const ViewSectionsModal = ({ template, onClose }) => {
  const sections = Array.isArray(template.sections) ? template.sections : [];
  const [shown, setShown] = useState(false);

  // Mount off-screen, then animate in on the next frame.
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Play the slide-out before unmounting.
  const handleClose = () => {
    setShown(false);
    setTimeout(onClose, 250);
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000]">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`}
        onClick={handleClose}
      />
      <div
        className={`absolute top-0 right-0 h-full w-full max-w-[580px] bg-white shadow-2xl flex flex-col font-outfit transition-transform duration-300 ease-out ${shown ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E4E4E7]">
          <button
            onClick={handleClose}
            className="shrink-0 w-8 h-8 -ml-1 rounded-[8px] flex items-center justify-center text-[#3F3F46] hover:bg-[#F4F4F5]"
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="text-[16px] font-semibold text-[#18181B] truncate">{template.name}</h2>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {sections.length === 0 ? (
            <div className="h-full flex items-center justify-center text-[14px] text-[#A1A1AA]">
              This template has no sections.
            </div>
          ) : (
            sections.map((sec, i) => {
              const subs = Array.isArray(sec?.subsections) ? sec.subsections : [];
              return (
                // Wrapper is inset so the divider isn't edge to edge; inner block is
                // rounded and carries the hover highlight (#F5F5F5).
                <div key={i} className={`mx-3 py-1.5 ${i < sections.length - 1 ? "border-b border-[#DEDEDE]" : ""}`}>
                  <div className="rounded-[6px] px-4 py-2.5 hover:bg-[#F5F5F5] transition-colors">
                    <div className="text-[15px] font-semibold text-[#A04A97]">
                      {i + 1}. {sec?.title || "Untitled section"}
                    </div>
                    {subs.length > 0 ? (
                      <div className="mt-2.5 flex flex-col gap-2.5">
                        {subs.map((sub, j) => (
                          <div key={j} className="flex items-start gap-2 text-[14px] text-[#3F3F46]">
                            <span className="text-[#52525B] tabular-nums shrink-0">{i + 1}.{j + 1}</span>
                            <span>{sub?.title || "Untitled"}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 text-[15px] text-[#A1A1AA]">—</div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const VISIBILITY_OPTIONS = [
  { value: "personal", label: "Personal (only me)" },
  { value: "organization", label: "Organization" },
];

// Custom visibility dropdown — a styled button + floating option panel (a native
// <select> can't render the rounded panel / gray hover highlight from the design).
const VisibilityDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = VISIBILITY_OPTIONS.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-[8px] border text-[14px] text-[#3F3F46] bg-white transition-colors ${
          open ? "border-[#A04A97] ring-1 ring-[#A04A97]" : "border-[#E4E4E7] hover:bg-[#FAFAFA]"
        }`}
      >
        <span>{selected?.label}</span>
        <svg className={`w-4 h-4 text-[#71717A] transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-[#E4E4E7] rounded-[12px] shadow-lg p-1.5">
          {VISIBILITY_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2.5 rounded-[8px] text-[15px] hover:bg-[#F4F4F5] ${
                o.value === value ? "text-[#18181B] font-medium" : "text-[#3F3F46]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// A single section card: numbered title field + remove button, and a
// collapsible Sub-Sections group with a caret + live count.
const SectionCard = ({ index, section, onChange, onRemove }) => {
  const subs = section.subsections;
  const setTitle = (title) => onChange({ ...section, title });
  const toggleOpen = () => onChange({ ...section, open: !section.open });
  const addSub = () =>
    onChange({ ...section, open: true, subsections: [...subs, { title: "" }] });
  const setSub = (j, title) =>
    onChange({ ...section, subsections: subs.map((s, idx) => (idx === j ? { title } : s)) });
  const removeSub = (j) =>
    onChange({ ...section, subsections: subs.filter((_, idx) => idx !== j) });

  return (
    <div className="bg-[#F4F4F5] rounded-[10px] p-3">
      <div className="flex items-center gap-2.5">
        {/* Numbered title field — bold index prefix inside the input box. */}
        <div className="flex-1 flex items-center gap-1.5 bg-white rounded-[8px] border border-[#E4E4E7] px-3 py-2 focus-within:border-[#A04A97] focus-within:ring-1 focus-within:ring-[#A04A97]">
          <span className="text-[14px] font-semibold text-[#18181B] tabular-nums">{index + 1}.</span>
          <input
            value={section.title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="New Section"
            className="flex-1 min-w-0 text-[14px] text-[#3F3F46] bg-transparent focus:outline-none"
          />
        </div>
        <button
          onClick={onRemove}
          className="shrink-0 w-8 h-8 rounded-[6px] flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2]"
          aria-label="Remove section"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Sub-Sections toggle */}
      <button
        onClick={toggleOpen}
        className="mt-2.5 flex items-center gap-1.5 text-[14px] text-[#52525B] hover:text-[#18181B]"
      >
        <svg className={`w-3.5 h-3.5 transition-transform ${section.open ? "rotate-90" : ""}`} viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5l8 7-8 7z" />
        </svg>
        Sub-Sections ({subs.length})
      </button>

      {section.open && (
        <div className="mt-2 pl-5 flex flex-col gap-2">
          {subs.map((sub, j) => (
            <div key={j} className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1.5 bg-white rounded-[8px] border border-[#E4E4E7] px-3 py-1.5 focus-within:border-[#A04A97] focus-within:ring-1 focus-within:ring-[#A04A97]">
                <span className="text-[13px] font-medium text-[#71717A] tabular-nums">{index + 1}.{j + 1}</span>
                <input
                  value={sub.title}
                  onChange={(e) => setSub(j, e.target.value)}
                  placeholder="New Sub-Section"
                  className="flex-1 min-w-0 text-[13px] text-[#3F3F46] bg-transparent focus:outline-none"
                />
              </div>
              <button
                onClick={() => removeSub(j)}
                className="shrink-0 w-7 h-7 rounded-[6px] flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2]"
                aria-label="Remove sub-section"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
          <button onClick={addSub} className="self-start text-[13px] font-medium text-[#A04A97] hover:text-[#8A3F83]">
            + Add Sub-Section
          </button>
        </div>
      )}
    </div>
  );
};

// Builder modal to create or edit a proposal section template: name, visibility,
// and a dynamic list of sections, each with optional sub-sections. Pass `initial`
// to prefill for editing; omit it to start a blank create form.
const CreateTemplateModal = ({ onClose, onSubmit, saving, initial, lockedVisibility }) => {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [visibility, setVisibility] = useState(lockedVisibility || initial?.visibility || "personal");
  const [sections, setSections] = useState(() =>
    (initial?.sections || []).map((s) => ({
      title: s?.title || "",
      open: false,
      subsections: (s?.subsections || []).map((sub) => ({ title: sub?.title || "" })),
    }))
  );
  const nameRef = useRef(null);
  const listRef = useRef(null);
  const prevCount = useRef(0);
  useEffect(() => nameRef.current?.focus(), []);

  // When a section is added, scroll the list to the bottom so the new card is
  // visible (only on growth — not on remove).
  useEffect(() => {
    if (sections.length > prevCount.current && listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }
    prevCount.current = sections.length;
  }, [sections.length]);

  const addSection = () =>
    setSections((prev) => [...prev, { title: "", open: false, subsections: [] }]);
  const updateSection = (i, next) =>
    setSections((prev) => prev.map((s, idx) => (idx === i ? next : s)));
  const removeSection = (i) =>
    setSections((prev) => prev.filter((_, idx) => idx !== i));

  // Backend drops empty titles and requires ≥1 section, so mirror that here.
  const canSave =
    name.trim().length > 0 && sections.some((s) => s.title.trim().length > 0) && !saving;

  const handleSave = () => {
    if (!canSave) return;
    const cleaned = sections
      .map((s) => ({
        title: s.title.trim(),
        subsections: s.subsections
          .map((sub) => ({ title: sub.title.trim() }))
          .filter((sub) => sub.title),
      }))
      .filter((s) => s.title);
    onSubmit({ name: name.trim(), visibility, sections: cleaned });
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-[16px] shadow-xl w-full max-w-[620px] max-h-[85vh] flex flex-col font-outfit"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-7 pt-6 pb-3">
          <div className="flex items-start justify-between">
            <h2 className="text-[20px] font-semibold text-[#18181B]">{isEdit ? "Edit Template" : "Create Template"}</h2>
            <button
              onClick={onClose}
              className="shrink-0 -mr-1 w-8 h-8 rounded-[8px] flex items-center justify-center text-[#3F3F46] hover:bg-[#F4F4F5]"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <p className="text-[14px] text-[#71717A] mt-1">Define the structure with sections and sub-sections to be used in proposal.</p>
        </div>
        <div className="mx-7 border-t border-[#E4E4E7]" />

        {/* Body — Name/Visibility + Sections header stay fixed; only the
            sections list scrolls. Body itself keeps overflow visible so the
            visibility dropdown panel isn't clipped. */}
        <div className="flex-1 min-h-0 flex flex-col px-7 pt-5">
          {/* Name + Visibility (fixed) */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-[14px] font-semibold text-[#18181B] mb-1.5">Template Name*</label>
              <input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Risk Assessment Report"
                className="w-full px-3 py-2.5 rounded-[8px] border border-[#E4E4E7] text-[14px] text-[#3F3F46] focus:outline-none focus:border-[#A04A97] focus:ring-1 focus:ring-[#A04A97]"
              />
            </div>
            <div>
              <label className="block text-[14px] font-semibold text-[#18181B] mb-1.5">Visibility*</label>
              {lockedVisibility ? (
                <LockedField className="!h-[42px] !text-[14px] !font-normal">
                  {VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.label}
                </LockedField>
              ) : (
                <VisibilityDropdown value={visibility} onChange={setVisibility} />
              )}
            </div>
          </div>

          {/* Sections header (fixed) */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[14px] font-semibold text-[#18181B]">Sections</span>
            <button onClick={addSection} className="text-[14px] font-medium text-[#A04A97] hover:text-[#8A3F83] focus:outline-none">
              + Add Section{sections.length > 0 ? ` (${sections.length})` : ""}
            </button>
          </div>

          {/* Sections list (scrolls) */}
          <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pb-5">
            {sections.length === 0 ? (
              <div className="bg-[#F4F4F5] rounded-[10px] h-[220px] flex items-center justify-center text-[15px] text-[#71717A]">
                Start by adding your first section !
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {sections.map((sec, i) => (
                  <SectionCard
                    key={i}
                    index={i}
                    section={sec}
                    onChange={(next) => updateSection(i, next)}
                    onRemove={() => removeSection(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mx-7 border-t border-[#E4E4E7]" />
        <div className="flex justify-center gap-4 px-7 py-5">
          <button onClick={onClose} className="px-8 py-2.5 rounded-[8px] border border-[#A04A97] text-[#A04A97] text-[15px] font-semibold hover:bg-[#A04A97]/[0.06]">
            Cancel
          </button>
          <button
            disabled={!canSave}
            onClick={handleSave}
            className={`px-8 py-2.5 rounded-[8px] text-white text-[15px] font-semibold ${canSave ? "bg-[#A04A97] hover:bg-[#8A3F83]" : "bg-[#D8B3D3] cursor-not-allowed"}`}
          >
            {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create Template")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const GRID_COLS = "grid grid-cols-[minmax(0,1.6fr)_140px_160px_100px_40px] items-center gap-x-4";

const ProposalSections = () => {
  const { canManage, orgRole, roleKnown } = useSettings();
  const isEditor = !canManage && (orgRole === "Editor" || orgRole === "Member");
  const isReviewer = orgRole === "Reviewer";

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [memberNames, setMemberNames] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [menu, setMenu] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sortDir, setSortDir] = useState("desc");

  const MENU_WIDTH = 160;
  const MENU_HEIGHT = 74;
  const orgId = Cookies.get("selected_org_id");
  const me = useMemo(() => getCurrentUser(), []);

  const scopeOptions = [
    { key: "all", label: "All templates" },
    { key: "organization", label: "Organization" },
    ...(isReviewer ? [] : [{ key: "mine", label: "My templates" }]),
  ];

  useEffect(() => {
    if (isReviewer && scope === "mine") setScope("all");
  }, [isReviewer, scope]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!orgId) {
        setLoading(false);
        toast.error("No workspace selected");
        return;
      }
      setLoading(true);
      try {
        // The templates API only returns a user id, so the member list resolves "Created by".
        const [list, orgRes] = await Promise.all([
          listProposalSections(),
          api.get(`/api/v1/org/details/?organization_id=${orgId}`).catch(() => null),
        ]);
        if (cancelled) return;

        const names = {};
        const members = orgRes?.data?.members || [];
        members.forEach((m) => {
          if (m?.user_id != null) names[m.user_id] = m.full_name || m.username || m.email || "Unknown";
        });

        setMemberNames(names);
        setTemplates(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load proposal sections");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [orgId]);

  const isMine = (t) => me?.user_id != null && String(me.user_id) === String(t?.created_by_user_id);

  const createdByLabel = (userId) => {
    const mine = me?.user_id != null && String(me.user_id) === String(userId);
    const name = memberNames[userId] || (mine ? me?.email : null) || "Unknown";
    return mine ? `${name} (You)` : name;
  };

  const canEditRow = (t) => canManage || (isEditor && isMine(t));

  const openMenuFor = (e, t) => {
    e.stopPropagation();
    if (menu?.id === t.id) { setMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const openUp = rect.bottom + MENU_HEIGHT + 8 > window.innerHeight;
    const top = openUp ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4;
    setMenu({ id: t.id, template: t, top, left: rect.right - MENU_WIDTH });
  };

  // Close the row menu on any outside click or scroll (fixed coords go stale).
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menu]);

  const handleDelete = async () => {
    const t = confirmDelete;
    if (!t) return;
    setDeleting(true);
    try {
      await deleteProposalSection(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      setConfirmDelete(null);
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  const closeForm = () => { setShowCreate(false); setEditing(null); };

  const handleSubmit = async (payload) => {
    setCreating(true);
    try {
      if (editing) {
        const updated = await updateProposalSection(editing.id, payload);
        setTemplates((prev) => prev.map((t) => (t.id === editing.id ? updated : t)));
        toast.success("Template updated");
      } else {
        const created = await createProposalSection(payload);
        setTemplates((prev) => [created, ...prev]);
        toast.success("Template created");
      }
      closeForm();
    } catch (e) {
      toast.error(e?.response?.data?.error || `Failed to ${editing ? "update" : "create"} template`);
    } finally {
      setCreating(false);
    }
  };

  const visibleTemplates = useMemo(() => {
    if (canManage || !roleKnown) return templates;
    return templates.filter((t) => {
      if (t.visibility === "organization") return true;
      return isEditor && me?.user_id != null && String(me.user_id) === String(t.created_by_user_id);
    });
  }, [templates, canManage, roleKnown, isEditor, me]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = visibleTemplates.filter((t) => {
      if (scope === "organization" && t.visibility !== "organization") return false;
      if (scope === "mine" && !(me?.user_id != null && String(me.user_id) === String(t.created_by_user_id))) return false;
      if (q && !(t.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortDir === "desc" ? db - da : da - db;
    });
    return rows;
  }, [visibleTemplates, search, scope, sortDir, me]);

  const headerAction = canManage ? (
    <PrimaryButton icon={PiPlus} onClick={() => { setEditing(null); setShowCreate(true); }}>
      New template
    </PrimaryButton>
  ) : isEditor ? (
    <PrimaryButton icon={PiPlus} onClick={() => { setEditing(null); setShowCreate(true); }}>
      New personal template
    </PrimaryButton>
  ) : null;

  const sectionsLabel = (t) => {
    if (!Array.isArray(t.sections)) return "View";
    const n = t.sections.length;
    return `${n} ${n === 1 ? "section" : "sections"}`;
  };

  return (
    <div className="font-outfit w-full">
      <PageHeader
        title="Proposal sections"
        description="Reusable section outlines that new proposals start from."
        action={headerAction}
      />

      {isEditor && (
        <ViewOnlyBanner icon={PiRows} pill="Limited">
          You can create personal templates. Organization templates are managed by admins.
        </ViewOnlyBanner>
      )}
      {isReviewer && (
        <ViewOnlyBanner icon={PiRows}>
          You can browse the section templates your team uses.
        </ViewOnlyBanner>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex items-center p-[3px] bg-[#F4F4F5] rounded-[8px]">
          {scopeOptions.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setScope(o.key)}
              className={`h-[30px] px-3 rounded-[6px] text-[13px] font-medium transition-colors ${
                scope === o.key
                  ? "bg-white text-[#18101C] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                  : "text-[#71717A] hover:text-[#3F3F46]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div className="relative w-[260px] max-w-full">
          <PiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-[#A1A1AA] pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates"
            className="w-full h-9 pl-9 pr-3 rounded-[8px] border border-black/[0.12] bg-white text-[14px] text-[#18101C] placeholder:text-[#A1A1AA] outline-none focus:border-[#8B55DD] focus:shadow-[0_0_0_3px_rgba(139,85,221,0.15)] transition-shadow"
          />
        </div>
      </div>

      <div className="border border-black/10 rounded-[12px] overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <div className={`${GRID_COLS} px-4 py-2.5 bg-[#F9FAFB] text-[13px] font-semibold text-[#52525B]`}>
              <div className="flex items-center gap-1.5 min-w-0">
                <span>Template name</span>
                <button
                  type="button"
                  onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  title={`Sort by date created (${sortDir === "desc" ? "newest first" : "oldest first"})`}
                  aria-label="Toggle sort by date created"
                  className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-black/[0.05] transition-colors"
                >
                  <SortArrows dir={sortDir} />
                </button>
              </div>
              <div>Visible to</div>
              <div>Created by</div>
              <div>Sections</div>
              <div />
            </div>

            {loading ? (
              <div className="p-8 text-center text-[14px] text-[#71717A] border-t border-black/[0.06]">
                Loading templates...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-[14px] text-[#71717A] border-t border-black/[0.06]">
                {visibleTemplates.length === 0
                  ? "No proposal section templates yet."
                  : "No templates match your search."}
              </div>
            ) : (
              filtered.map((t) => (
                <div
                  key={t.id}
                  className={`${GRID_COLS} px-4 py-3 border-t border-black/[0.06] hover:bg-[#FCFAFD] transition-colors`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <PiListBullets className="shrink-0 text-[16px] text-[#A04A97]" />
                    <span className="truncate text-[14px] font-normal text-[#18101C]">{t.name}</span>
                  </div>
                  <div>
                    {t.visibility === "organization" ? (
                      <StatusPill tone="purple">Organization</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Personal</StatusPill>
                    )}
                  </div>
                  <div className="truncate text-[14px] text-[#3F3F46]">{createdByLabel(t.created_by_user_id)}</div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setViewing(t)}
                      className="text-[14px] font-medium text-[#A04A97] hover:text-[#8E4186] hover:underline underline-offset-2"
                    >
                      {sectionsLabel(t)}
                    </button>
                  </div>
                  <div className="flex justify-center">
                    {canEditRow(t) && (
                      <button
                        type="button"
                        onClick={(e) => openMenuFor(e, t)}
                        aria-label="Row actions"
                        className={`w-8 h-8 flex items-center justify-center rounded-[6px] text-[18px] text-[#52525B] transition-colors ${menu?.id === t.id ? "bg-black/[0.05]" : "hover:bg-black/[0.04]"}`}
                      >
                        <PiDotsThree />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {menu && (
        <RowActionMenu
          pos={menu}
          onEdit={() => { setEditing(menu.template); setShowCreate(false); setMenu(null); }}
          onDelete={() => { setConfirmDelete(menu.template); setMenu(null); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          name={confirmDelete.name}
          deleting={deleting}
          onClose={() => !deleting && setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}

      {viewing && <ViewSectionsModal template={viewing} onClose={() => setViewing(null)} />}

      {(showCreate || editing) && (
        <CreateTemplateModal
          initial={editing}
          saving={creating}
          lockedVisibility={isEditor ? (editing ? editing.visibility : "personal") : undefined}
          onClose={() => !creating && closeForm()}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export default ProposalSections;
